import React from 'react';
import * as Slider from '@radix-ui/react-slider';
import cn from '../utils/cn';

type KoobInputSliderProps = {
  /** One entry per thumb: [value] for a single slider, [min, max] for a range. */
  value: number[];
  onValueChange: (value: number[]) => void;
  /** Fires once when the user releases a thumb — use for expensive refetches. */
  onValueCommit?: (value: number[]) => void;
  min?: number;
  max?: number;
  step?: number;
  minStepsBetweenThumbs?: number;
  disabled?: boolean;
  /** Render the current value(s) under the slider ends. */
  showValues?: boolean;
  formatValue?: (value: number) => React.ReactNode;
  'data-testid'?: string;
};

export default function KoobInputSlider({
  value,
  onValueChange,
  onValueCommit,
  min = 0,
  max = 100,
  step = 1,
  minStepsBetweenThumbs = 1,
  disabled = false,
  showValues = true,
  formatValue = (v) => v,
  ...props
}: KoobInputSliderProps) {
  return (
    <div className="w-full">
      <Slider.Root
        className={cn(
          'relative flex h-5 w-full touch-none select-none items-center',
          disabled && 'cursor-not-allowed opacity-50',
        )}
        value={value}
        onValueChange={onValueChange}
        onValueCommit={onValueCommit}
        min={min}
        max={max}
        step={step}
        minStepsBetweenThumbs={
          value.length > 1 ? minStepsBetweenThumbs : undefined
        }
        disabled={disabled}
        {...props}
      >
        <Slider.Track className="relative h-1.5 grow overflow-hidden rounded-full bg-brand/20">
          <Slider.Range className="absolute h-full bg-brand" />
        </Slider.Track>
        {value.map((_, index) => (
          <Slider.Thumb
            key={index}
            className="block size-4 cursor-grab rounded-full border border-solid border-brand bg-white shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring active:cursor-grabbing disabled:pointer-events-none"
          />
        ))}
      </Slider.Root>
      {showValues && (
        <div className="mt-1 flex justify-between text-xs text-gray-500">
          <span>{formatValue(value[0])}</span>
          {value.length > 1 && (
            <span>{formatValue(value[value.length - 1])}</span>
          )}
        </div>
      )}
    </div>
  );
}
